import React, { useState, useRef, useEffect } from 'react';
import './Carousel.css';
import Modal from '../Modal/Modal';

const ShoppingBagIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" />
    <path d="M3 6h18" />
    <path d="M16 10a4 4 0 01-8 0" />
  </svg>
);

const ArrowLeftIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M15 18l-6-6 6-6" />
  </svg>
);

const ArrowRightIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 18l6-6-6-6" />
  </svg>
);

const Carousel = ({ items, onAction }) => {
    const [modalOpen, setModalOpen] = useState(false);
    const [modalProduct, setModalProduct] = useState(null);
    const [modalImages, setModalImages] = useState([]);
    const carouselRef = useRef(null);
    const hasInitiallyCentered = useRef(false);
    const currentScrollPosition = useRef(0);

    // Center the first item only on initial mount, not on data updates
    useEffect(() => {
        const centerFirstItem = () => {
            if (carouselRef.current && items.length > 0 && !hasInitiallyCentered.current) {
                const container = carouselRef.current;
                const containerWidth = container.clientWidth;
                const firstItem = container.querySelector('.carousel-item');
                
                if (firstItem) {
                    // Center the first item
                    const scrollLeft = firstItem.offsetLeft - (containerWidth / 2) + (firstItem.offsetWidth / 2);
                    container.scrollLeft = Math.max(0, scrollLeft);
                    currentScrollPosition.current = container.scrollLeft;
                    hasInitiallyCentered.current = true;
                }
            } else if (carouselRef.current && hasInitiallyCentered.current) {
                // If we've already centered initially, preserve the current scroll position
                // This prevents polling from resetting the scroll position
                const container = carouselRef.current;
                if (currentScrollPosition.current > 0) {
                    container.scrollLeft = currentScrollPosition.current;
                }
            }
        };

        // Small delay to ensure DOM is fully rendered
        const timer = setTimeout(centerFirstItem, 100);
        return () => clearTimeout(timer);
    }, [items]);

    // Track scroll position changes
    useEffect(() => {
        const container = carouselRef.current;
        if (container) {
            const handleScroll = () => {
                currentScrollPosition.current = container.scrollLeft;
            };
            
            container.addEventListener('scroll', handleScroll);
            return () => container.removeEventListener('scroll', handleScroll);
        }
    }, []);

    const handleViewDetails = (item) => {
        setModalProduct(item);
        setModalImages(item.allImages && item.allImages.length > 0 ? item.allImages : [item.productUrl]);
        setModalOpen(true);
    };

    const scrollCarousel = (direction) => {
        if (carouselRef.current) {
            const container = carouselRef.current;
            const containerWidth = container.clientWidth;
            
            // Get all carousel items
            const items = container.querySelectorAll('.carousel-item');
            if (items.length === 0) return;
            
            // Find the currently visible item
            let currentIndex = 0;
            for (let i = 0; i < items.length; i++) {
                const item = items[i];
                const itemRect = item.getBoundingClientRect();
                const containerRect = container.getBoundingClientRect();
                
                // Check if item is in the center area of the container
                if (itemRect.left <= containerRect.left + containerWidth / 2 && 
                    itemRect.right >= containerRect.left + containerWidth / 2) {
                    currentIndex = i;
                    break;
                }
            }
            
            // Calculate next/previous index
            let targetIndex;
            if (direction === 'left') {
                targetIndex = Math.max(0, currentIndex - 1);
            } else {
                targetIndex = Math.min(items.length - 1, currentIndex + 1);
            }
            
            // Scroll to center the target item
            const targetItem = items[targetIndex];
            if (targetItem) {
                const scrollLeft = targetItem.offsetLeft - (containerWidth / 2) + (targetItem.offsetWidth / 2);
                
                container.scrollTo({
                    left: Math.max(0, scrollLeft),
                    behavior: 'smooth'
                });
                
                // Update the tracked scroll position
                currentScrollPosition.current = Math.max(0, scrollLeft);
            }
        }
    };

    return (
        <>
        <div className="carousel-wrapper">
            <button 
                className="carousel-scroll-button left" 
                onClick={() => scrollCarousel('left')}
                aria-label="Scroll left"
            >
                <ArrowLeftIcon />
            </button>
            <div className="carousel-container" ref={carouselRef}>
                {items.map((item, index) => {
                    console.log('Carousel item:', item); // DEBUG: Check if size is present
                    const images = item.allImages && item.allImages.length > 0
                        ? item.allImages
                        : item.productUrl ? [item.productUrl] : [];
                    const currentImg = images[0] || 'https://via.placeholder.com/400x300?text=Image+Not+Found';
                    return (
                        <div 
                            key={index} 
                            className="carousel-item editorial-card"
                            onClick={() => handleViewDetails(item)}
                        >
                            <img
                                src={currentImg}
                                alt={item.title}
                                className="carousel-image-full"
                                onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = 'https://via.placeholder.com/400x300?text=Image+Not+Found';
                                }}
                            />
                            <div className="carousel-info-editorial">
                                <h3 className="carousel-title-editorial" title={item.title}>{item.title}</h3>
                                
                                {/* Price Section */}
                                <div className="carousel-price-section">
                                    <div className="carousel-price-row">
                                        <span className="carousel-current-price">Rs. {item.price}</span>
                                        {item.compare_at_price && item.compare_at_price > item.price && Math.round(((item.compare_at_price - item.price) / item.compare_at_price) * 100) > 0 && (
                                            <>
                                                <span className="carousel-compare-price">Rs. {item.compare_at_price}</span>
                                                <span className="carousel-discount-badge">
                                                    {Math.round(((item.compare_at_price - item.price) / item.compare_at_price) * 100)}% OFF
                                                </span>
                                            </>
                                        )}
                                    </div>
                                    
                                    {/* Size Section */}
                                    {item.size && (
                                        <div className="carousel-size-row">
                                            <span className="carousel-size-badge">Size: {item.size}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="carousel-actions-editorial">
                                <button onClick={(e) => { e.stopPropagation(); handleViewDetails(item); }}>
                                    View Details
                                </button>
                                <button onClick={(e) => { e.stopPropagation(); onAction && onAction(e, 'buy', { ...item, size: item.size }); }}>
                                    Buy Now <ShoppingBagIcon />
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>
            <button 
                className="carousel-scroll-button right" 
                onClick={() => scrollCarousel('right')}
                aria-label="Scroll right"
            >
                <ArrowRightIcon />
            </button>
        </div>
        <Modal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          title={modalProduct?.title}
          price={modalProduct?.price}
          compare_at_price={modalProduct?.compare_at_price}
          size={modalProduct?.size}
          images={modalImages}
          actions={modalProduct ? [
            {
              label: 'Buy Now',
              onClick: () => onAction && onAction(null, 'buy', { ...modalProduct, size: modalProduct?.size }),
              variant: 'primary'
            }
          ] : []}
        />
        </>
    );
};

export default Carousel;